import isEmpty from "lodash/isEmpty";
import { createJSONStorage } from "zustand/middleware";

import { User } from "../../Common/models/user";
import {
  AdminModuleKey,
  AdminRolePermissions,
} from "../../Common/models/adminRole";
import { isSuperAdminUser } from "../../Common/utils/isSuperAdminUser";
import * as capacitorStorage from "../../Common/utils/localStorage";
import { LOCAL_STORAGE } from "../../Common/config";
import { createWithEqualityFn } from "zustand/traditional";
import { persist } from "zustand/middleware";
import {
  bootstrapAdminRolePermissions,
  teardownAdminRoleListener,
} from "../services/adminRoles";
import { teardownUserSession } from "../services/userSession";

interface UserState {
  user: User | null | undefined;
  rolePermissions: AdminRolePermissions | null;
  isSuperAdmin: () => boolean;
  hasPermission: (module: AdminModuleKey, action: "view" | "edit") => boolean;
  setUser: (state: User) => void;
  updateUser: (state: Partial<User>) => void;
  setRolePermissions: (permissions: AdminRolePermissions | null) => void;
  removeUser: () => Promise<void>;
}

export const useUserStore = createWithEqualityFn<
  UserState,
  [["zustand/persist", UserState]]
>(
  persist(
    (set, get) => ({
      user: undefined as User | null | undefined,
      rolePermissions: null,
      setUser: (newState: User) => {
        set(() => ({ user: newState }));
        const { firstName, lastName, emailAddress } = newState;
        capacitorStorage.setItem(
          LOCAL_STORAGE.userName,
          `${firstName} ${lastName}`
        );
        capacitorStorage.setItem(LOCAL_STORAGE.userEmail, emailAddress);
      },
      updateUser: (updateState: Partial<User>) => {
        const existing = get().user || ({} as User);
        return set(() => ({ user: { ...existing, ...updateState } }));
      },
      setRolePermissions: (permissions: AdminRolePermissions | null) => {
        set(() => ({ rolePermissions: permissions }));
      },
      isSuperAdmin: () => {
        const user = get().user;
        return user !== undefined && !isEmpty(user) && isSuperAdminUser(user);
      },
      hasPermission: (module, action) => {
        const { isSuperAdmin, rolePermissions } = get();
        if (isSuperAdmin()) {
          return true;
        }
        if (!rolePermissions) {
          return false;
        }
        return rolePermissions[module]?.[action] === true;
      },
      removeUser: async () => {
        teardownAdminRoleListener();
        teardownUserSession();
        return set(() => ({ user: null, rolePermissions: null }));
      },
    }),
    {
      name: "user-detail-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) =>
        ({
          user: state.user,
          rolePermissions: state.rolePermissions,
        }) as UserState,
    }
  )
);

export const initializeAdminRolePermissions = async (
  user: User
): Promise<void> => {
  const { updateUser, setRolePermissions } = useUserStore.getState();
  await bootstrapAdminRolePermissions({
    user,
    updateUser,
    setRolePermissions,
  });
};
